import React, { useState } from 'react';
import IpsScanFileRow from './IpsScanFileRow';

const STATUS_BADGES = {
  queued: { label: 'Queued', className: 'bg-secondary' },
  scanning: { label: 'Scanning...', className: 'bg-primary' },
  found: { label: 'IPS Found', className: 'bg-success' },
  missing: { label: 'No Match', className: 'bg-danger' },
  error: { label: 'Error', className: 'bg-warning text-dark' },
};

/**
 * IpsScanClientTable
 *
 * Renders the live, per-client progress of an IPS scan: each row starts as
 * "Queued", flips to "Scanning..." while its request is in flight, and then
 * settles on "IPS Found" (with the matching file(s) expandable), "No Match",
 * or "Error".
 */
const IpsScanClientTable = ({
  rows,
  sortBy,
  sortDirection,
  onSortChange,
  fileAnalyses,
  missingRequiredByClient = {},
  isConfirmedNonIps = () => false,
  renameTargetFileIdsByClient = {},
  renameDrafts = {},
  selectedRenameFileIds = {},
  renamingFileIds = {},
  onRenameDraftChange = null,
  onToggleRenameSelect = null,
  onRenameFile = null,
  onSetRenameTarget = null,
}) => {
  const [expandedClient, setExpandedClient] = useState(null);

  const toggleExpanded = (clientName) => {
    setExpandedClient((prev) => (prev === clientName ? null : clientName));
  };

  const renderSortIcon = (columnKey) => {
    const isActive = sortBy === columnKey;
    const icon = !isActive
      ? 'bi-arrow-down-up'
      : sortDirection === 'asc'
        ? 'bi-caret-up-fill'
        : 'bi-caret-down-fill';

    return (
      <i
        aria-hidden="true"
        className={`bi ${icon} ms-1 ${isActive ? 'text-primary' : 'text-muted'}`}
        style={{ fontSize: '0.72rem' }}
      ></i>
    );
  };

  return (
    <div className="table-responsive" style={{ maxHeight: '640px', overflowY: 'auto' }}>
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
          <tr>
            <th>
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                onClick={() => onSortChange('client')}
              >
                Client
                {renderSortIcon('client')}
              </button>
            </th>
            <th style={{ width: '160px' }}>
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                onClick={() => onSortChange('status')}
              >
                Status
                {renderSortIcon('status')}
              </button>
            </th>
            <th style={{ minWidth: '260px' }}>Current Filename</th>
            <th style={{ minWidth: '320px' }}>Suggested Filename</th>
            <th style={{ width: '130px' }}>Select Target</th>
            <th style={{ width: '130px' }}>Rename Target</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="text-muted text-center py-3">
                No clients to show.
              </td>
            </tr>
          )}
          {rows.map((row) => {
            // Only show files that have NOT been confirmed as non-IPS by the LLM.
            const visibleFiles = row.status === 'found' && row.files
              ? row.files.filter((f) => !isConfirmedNonIps(f.fileId))
              : [];
            // Derive an effective badge: if status is 'found' but all matches were
            // confirmed non-IPS (no visible files), treat it as 'missing' so the
            // badge reflects what the UI is actually showing.
            const effectiveStatus = row.status === 'found' && visibleFiles.length === 0
              ? 'missing'
              : row.status;
            const badge = STATUS_BADGES[effectiveStatus] || STATUS_BADGES.queued;
            const missingReason = (row.reasonNote || '').trim();
            const badgeLabel = (
              effectiveStatus === 'missing' && missingReason
                ? missingReason
                : badge.label
            );
            const hasFiles = visibleFiles.length > 0;
            const missingRequiredInfo = missingRequiredByClient[row.clientName] || {
              hasMissingRequired: false,
              missingFieldLabels: [],
            };
            const latestFileId = renameTargetFileIdsByClient[row.clientName];
            const latestFile = latestFileId
              ? visibleFiles.find((file) => file.fileId === latestFileId) || null
              : null;
            const renameDraft = latestFile ? (renameDrafts?.[latestFile.fileId]?.value || '') : '';
            const suggestedName = latestFile ? (renameDrafts?.[latestFile.fileId]?.suggestedName || '') : '';
            const effectiveDraft = renameDraft || suggestedName;
            const isSelected = latestFile ? !!selectedRenameFileIds?.[latestFile.fileId] : false;
            const isRenaming = latestFile ? !!renamingFileIds?.[latestFile.fileId] : false;
            const displayFileName = latestFile ? (latestFile.renamedName || latestFile.name) : '';

            return (
              <React.Fragment key={row.clientName}>
                <tr>
                  <td>{row.clientName}</td>
                  <td>
                    <span className={`badge ${badge.className}`}>
                      {row.status === 'scanning' && (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      )}
                      {badgeLabel}
                    </span>
                  </td>
                  <td>
                    {latestFile ? (
                      <div className="d-inline-flex align-items-center gap-2 flex-wrap">
                        {latestFile.webUrl ? (
                          <a href={latestFile.webUrl} target="_blank" rel="noopener noreferrer">
                            {displayFileName}
                          </a>
                        ) : (
                          <span>{displayFileName}</span>
                        )}
                        {latestFile.renameStatus === 'renamed' && (
                          <span className="badge bg-success-subtle text-success-emphasis border border-success-subtle">
                            Renamed
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {latestFile ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={effectiveDraft}
                        onChange={(event) => onRenameDraftChange?.(row.clientName, latestFile, event.target.value)}
                        placeholder="Last-First-IPS-Signed-YYYY-MM-DD.pdf"
                        disabled={isRenaming}
                      />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {latestFile ? (
                      <div className="form-check mb-0 d-flex justify-content-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={isSelected}
                          onChange={(event) => onToggleRenameSelect?.(row.clientName, latestFile, event.target.checked)}
                          disabled={isRenaming}
                          aria-label={`Select ${row.clientName} target for bulk rename and export`}
                        />
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {latestFile ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => onRenameFile?.(row.clientName, latestFile)}
                        disabled={isRenaming || !effectiveDraft.trim()}
                      >
                        {isRenaming ? 'Renaming...' : 'Rename'}
                      </button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {row.status === 'error' && (
                      <span className="text-danger small">{row.error}</span>
                    )}
                    {effectiveStatus === 'missing' && missingReason && (
                      <span className="text-muted small">{missingReason}</span>
                    )}
                    {row.status === 'found' && missingRequiredInfo.hasMissingRequired && (
                      <span
                        className="badge bg-warning text-dark me-2"
                        title={`Missing required fields: ${missingRequiredInfo.missingFieldLabels.join(', ')}`}
                      >
                        Missing Required Fields
                      </span>
                    )}
                    {hasFiles && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => toggleExpanded(row.clientName)}
                      >
                        {visibleFiles.length} file{visibleFiles.length > 1 ? 's' : ''}
                        <i className={`bi bi-chevron-${expandedClient === row.clientName ? 'up' : 'down'} ms-1`}></i>
                      </button>
                    )}
                  </td>
                </tr>
                {hasFiles && expandedClient === row.clientName && (
                  <tr>
                    <td colSpan={7} className="bg-light-subtle">
                      <ul className="list-unstyled mt-2 mb-1 small">
                        {visibleFiles.map((file) => (
                          <IpsScanFileRow
                            key={file.fileId || file.path}
                            file={file}
                            analysis={fileAnalyses?.[file.fileId]}
                            isLatestForRename={renameTargetFileIdsByClient[row.clientName] === file.fileId}
                            suggestedName={renameDrafts[file.fileId]?.suggestedName || ''}
                            draftName={renameDrafts[file.fileId]?.value || ''}
                            isSelected={!!selectedRenameFileIds[file.fileId]}
                            isRenaming={!!renamingFileIds[file.fileId]}
                            onDraftNameChange={(value) => onRenameDraftChange?.(row.clientName, file, value)}
                            onToggleSelect={(checked) => onToggleRenameSelect?.(row.clientName, file, checked)}
                            onRenameFile={() => onRenameFile?.(row.clientName, file)}
                            showRenameControls={false}
                            isRenameTarget={renameTargetFileIdsByClient[row.clientName] === file.fileId}
                            canSetAsRenameTarget={visibleFiles.length > 1}
                            onSetAsRenameTarget={() => onSetRenameTarget?.(row.clientName, file)}
                          />
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default IpsScanClientTable;
